import { Box, Button, Icon } from '@chakra-ui/react'
import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react'
import type { VirtuosoGridHandle } from 'react-virtuoso'
import { VirtuosoGrid } from 'react-virtuoso'
import type Selecto from 'react-selecto'
import { FiChevronDown } from 'react-icons/fi'
import useColCountObserver from '../../../../hooks/useColCountObserver'
import type { MediaLibraryActionType } from '../../../../providers/MLProvider'
import { MediaLibraryActions } from '../../../../providers/MLProvider'
import type {
  MediaLibrarySearchFileResponse,
  MediaLibrarySearchFolderResponse,
  MediaLibrarySearchCollectionResponse,
} from '../../../../interfaces'
import {
  foldersPerPage,
  filesPerPage,
  mediaCollectionsPerPage,
} from '../../../../../utils/mediaLibrary'
import useGridKeyboardNavigation from '../../../../hooks/useKeyboardSelectionv2'
import makeGridComponents from './makeGridComponents'
import Heading from './Heading'
import NewFolderElement from './NewFolderElement'
import NewFileElement from './NewFileElement'
import NewCollectionElement from './NewCollectionElement'
import DragComponent from './Selecto'

interface SearchGridViewProps {
  scrollEl: HTMLDivElement | null
  dispatch: React.Dispatch<MediaLibraryActionType | MediaLibraryActionType[]>
  fileList: MediaLibrarySearchFileResponse[]
  folderList: MediaLibrarySearchFolderResponse[]
  mediaCollectionList: MediaLibrarySearchCollectionResponse[]
  selectedFiles: MediaLibrarySearchFileResponse[]
  selectedFolders: MediaLibrarySearchFolderResponse[]
  selectedMediaCollections: MediaLibrarySearchCollectionResponse[]
  loadingFiles: boolean
  loadingFolders: boolean
  loadingMediaCollections: boolean
  filePage: number
  folderPage: number
  mediaCollectionPage: number
  minHeight: string
}

const SearchGridView = React.memo(
  ({
    scrollEl,
    dispatch,
    fileList,
    folderList,
    mediaCollectionList,
    selectedFiles,
    selectedFolders,
    selectedMediaCollections,
    loadingFiles,
    loadingFolders,
    loadingMediaCollections,
    filePage,
    folderPage,
    mediaCollectionPage,
    minHeight,
  }: SearchGridViewProps) => {
    const [colCount, setColCount] = useState(3)
    const [focusedIndex, setFocusedIndex] = useState<{
      index: number
      type: string
      id: string
    } | null>(null)
    const gridOrder = ['folder', 'file', 'mediaCollection']
    const assetCountMap = {
      folder: folderList.length,
      file: fileList.length,
      mediaCollection: mediaCollectionList.length,
    }

    const fileRef = useRef<VirtuosoGridHandle>(null)
    const folderRef = useRef<VirtuosoGridHandle>(null)
    const collectionRef = useRef<VirtuosoGridHandle>(null)

    const selectoRef = React.useRef<Selecto>(null)
    const keyActiveElementRef = React.useRef<HTMLDivElement | null>(null)

    const selectoDragMarker = React.useRef<{ x: number; y: number } | null>(
      null,
    )

    const assetRefMap = {
      folder: folderRef,
      file: fileRef,
      mediaCollection: collectionRef,
    }

      function isFileVersion(
        item: MediaLibrarySearchFileResponse,
      ): item is MediaLibrarySearchFileResponse {
        return item.type === 'file-version'
      }
    

    const getIdMap = useMemo(
      () => ({
        folder: (index: number) => folderList[index]?.folderId ?? '',
        file: (index: number) =>
         isFileVersion(fileList[index] as MediaLibrarySearchFileResponse) ? fileList[index]?.versionInfo?.id ?? fileList[index]?.fileId : fileList[index]?.fileId,
        mediaCollection: (index: number) =>
          mediaCollectionList[index]?.mediaCollectionId ?? '',
      }),
      [folderList, fileList, mediaCollectionList],
    )

    const selectedAssetMap: Record<
      'folder' | 'file' | 'mediaCollection',
      Set<string>
    > = useMemo(() => {
      const selectedFoldersLocal = new Set<string>()
      const selectedFilesLocal = new Set<string>()
      const selectedCollectionsLocal = new Set<string>()

      for (const f of selectedFolders) {
        selectedFoldersLocal.add(f.folderId)
      }

      for (const f of selectedFiles) {
        selectedFilesLocal.add(isFileVersion(f as MediaLibrarySearchFileResponse) ? f.versionInfo?.id ?? f.fileId : f.fileId)
      }

      for (const c of selectedMediaCollections) {
        selectedCollectionsLocal.add(c.mediaCollectionId)
      }

      return {
        folder: selectedFoldersLocal,
        file: selectedFilesLocal,
        mediaCollection: selectedCollectionsLocal,
      }
    }, [selectedFolders, selectedFiles, selectedMediaCollections])

    const setSelectedAssetMap = useMemo(
      () => ({
        folder: (newSet: Set<string>) => {
          const folders = folderList.filter(f => newSet.has(f.folderId))
          dispatch({
            type: MediaLibraryActions.SET_SELECTED_FOLDERS,
            payload: folders,
          })
        },
        file: (newSet: Set<string>) => {
          const files = fileList.filter(f =>
            newSet.has(isFileVersion(f as MediaLibrarySearchFileResponse) ? f.versionInfo?.id ?? f.fileId : f.fileId),
          )
          dispatch({
            type: MediaLibraryActions.SET_SELECTED_FILES,
            payload: files,
          })
        },
        mediaCollection: (newSet: Set<string>) => {
          const collections = mediaCollectionList.filter(c =>
            newSet.has(c.mediaCollectionId),
          )
          dispatch({
            type: MediaLibraryActions.SET_SELECTED_MEDIA_COLLECTIONS,
            payload: collections,
          })
        },
      }),
      [dispatch, folderList, fileList, mediaCollectionList],
    )

    const handleKeyDown = useGridKeyboardNavigation({
      gridOrder,
      assetCountMap,
      getIdMap,
      colCount,
      selectedAssetMap,
      setSelectedAssetMap,
      focusedIndex,
      setFocusedIndex,
      assetRefMap,
      dispatch,
      route: '/search',
    })

  const gridComponentsMap = useMemo(
    () => ({
      folder: makeGridComponents(colCount, 'folder-grid'),
      file: makeGridComponents(colCount, 'file-grid'),
      mediaCollection: makeGridComponents(colCount, 'mediaCollection-grid'),
    }),
    [colCount],
  )

  useColCountObserver(scrollEl, setColCount)

  const handleLoadMoreFiles = React.useCallback(
    e => {
      e.stopPropagation()
      dispatch({ type: MediaLibraryActions.LOAD_MORE_FILES })
    },
    [dispatch],
  )

    const handleLoadMoreFolders = React.useCallback(
      e => {
        e.stopPropagation()
        dispatch({ type: MediaLibraryActions.LOAD_MORE_FOLDERS })
      },
      [dispatch],
    )

    const handleLoadMoreCollections = React.useCallback(
      e => {
        e.stopPropagation()
        dispatch({ type: MediaLibraryActions.LOAD_MORE_MEDIA_COLLECTIONS })
      },
      [dispatch],
    )

    const refreshTargets = useCallback(
      () =>
        requestAnimationFrame(() =>
          selectoRef.current?.findSelectableTargets(),
        ),
      [selectoRef.current],
    )

     useEffect(() => {
       if (focusedIndex?.id) {
         const { index, type, id } = focusedIndex
         if (!selectedAssetMap[type].has(id)) {
           setFocusedIndex(null)
         }
       }
     }, [JSON.stringify(focusedIndex), selectedAssetMap])

    return (
      <Box
        height="100%"
        outline="none"
        width="100%"
        paddingX="24px"
        paddingTop={'24px'}
        ref={keyActiveElementRef}
        minHeight={minHeight}
        tabIndex={0}
        onClick={() => {
          // if (
          //   selectoDragMarker.current &&
          //   (Math.abs(selectoDragMarker.current.x) > 5 ||
          //     Math.abs(selectoDragMarker.current.y) > 5)
          // ) {
          //   selectoDragMarker.current = null
          //   return
          // }
          setFocusedIndex(null)
          Object.entries(setSelectedAssetMap).forEach(([type, selected]) => {
            selectedAssetMap[type].clear()
            selected(selectedAssetMap[type])
          })
        }}
        onKeyDown={handleKeyDown}
      >
        {scrollEl && (
          <>
            <DragComponent
              selectedAssetMap={selectedAssetMap}
              setSelectedAssetMap={setSelectedAssetMap}
              setFocusedIndex={setFocusedIndex}
              selectoRef={selectoRef}
              scrollEl={scrollEl}
              selectoDragMarker={selectoDragMarker}
              assetCountMap={assetCountMap}
              getIdMap={getIdMap}
              colCount={colCount}
              gridOrder={gridOrder}
              keyActiveElementRef={keyActiveElementRef}
            />

            {folderList.length > 0 && (
              <>
                <Heading name="Folders" isFirst={true} className='folder-heading'/>
                <VirtuosoGrid
                  data={folderList}
                  ref={folderRef}
                  customScrollParent={scrollEl}
                  components={gridComponentsMap.folder}
                  rangeChanged={refreshTargets}
                  overscan={0}
                  itemContent={(index, item) => (
                    <NewFolderElement
                      getIdMap={getIdMap}
                      setFocusedIndex={setFocusedIndex}
                      selectedAssetMap={selectedAssetMap}
                      setSelectedAssetMap={setSelectedAssetMap}
                      focusedIndex={focusedIndex}
                      index={index}
                      dispatch={dispatch}
                      mediaCollectionRoute={false}
                      currentMediaCollection=""
                      folder={item}
                      gridOrder={gridOrder}
                      assetCountMap={assetCountMap}
                      route="/search"
                    />
                  )}
                />
                {(folderList.length % foldersPerPage === 0 &&
                  folderList.length / foldersPerPage === folderPage + 1) ||
                loadingFolders ? (
                  <Button
                    as="a"
                    variant="link"
                    cursor="pointer"
                    color="appClearBlue"
                    paddingTop="16px"
                    onClick={handleLoadMoreFolders}
                    isLoading={loadingFolders}
                    loadingText="Loading..."
                    className='folder-see-more'
                  >
                    See More
                    <Icon as={FiChevronDown} />
                  </Button>
                ) : null}
              </>
            )}

            {fileList.length > 0 && (
              <>
                <Heading name="Files" isFirst={folderList.length === 0} className='file-heading'/>
                <VirtuosoGrid
                  data={fileList}
                  customScrollParent={scrollEl}
                  components={ gridComponentsMap.file}
                  overscan={0}
                  ref={fileRef}
                  rangeChanged={refreshTargets}
                  itemContent={(index, item) => (
                    <NewFileElement
                      getIdMap={getIdMap}
                      setFocusedIndex={setFocusedIndex}
                      selectedAssetMap={selectedAssetMap}
                      setSelectedAssetMap={setSelectedAssetMap}
                      focusedIndex={focusedIndex}
                      index={index}
                      dispatch={dispatch}
                      mediaCollectionRoute={false}
                      currentMediaCollection=""
                      folderPath=""
                      file={item}
                      gridOrder={gridOrder}
                      assetCountMap={assetCountMap}
                      route="/search"
                    />
                  )}
                />
                {(fileList.length % filesPerPage === 0 &&
                  fileList.length / filesPerPage === filePage + 1) ||
                loadingFiles ? (
                  <Button
                    as="a"
                    variant="link"
                    cursor="pointer"
                    color="appClearBlue"
                    paddingTop="16px"
                    onClick={handleLoadMoreFiles}
                    isLoading={loadingFiles}
                    loadingText="Loading..."
                    className='file-see-more'
                  >
                    See More
                    <Icon as={FiChevronDown} />
                  </Button>
                ) : null}
              </>
            )}
            {mediaCollectionList.length > 0 && (
              <>
                <Heading
                  name="Collections"
                  isFirst={fileList.length === 0 && folderList.length === 0}
                  className ='mediaCollection-heading'
                />
                <VirtuosoGrid
                  data={mediaCollectionList}
                  ref={collectionRef}
                  customScrollParent={scrollEl}
                  components={gridComponentsMap.mediaCollection}
                  rangeChanged={refreshTargets}
                  overscan={0}
                  itemContent={(index, item) => (
                    <NewCollectionElement
                      gridTypeForDataset="mediaCollection"
                      getIdMap={getIdMap}
                      setFocusedIndex={setFocusedIndex}
                      selectedAssetMap={selectedAssetMap}
                      setSelectedAssetMap={setSelectedAssetMap}
                      focusedIndex={focusedIndex}
                      index={index}
                      dispatch={dispatch}
                      mediaCollection={
                        item as MediaLibrarySearchCollectionResponse
                      }
                      gridOrder={gridOrder}
                      assetCountMap={assetCountMap}
                    />
                  )}
                />
                {(mediaCollectionList.length % mediaCollectionsPerPage === 0 &&
                  mediaCollectionList.length / mediaCollectionsPerPage ===
                    mediaCollectionPage + 1) ||
                loadingMediaCollections ? (
                  <Button
                    as="a"
                    variant="link"
                    cursor="pointer"
                    color="appClearBlue"
                    paddingTop="16px"
                    onClick={handleLoadMoreCollections}
                    isLoading={loadingMediaCollections}
                    loadingText="Loading..."
                    className="mediaCollection-see-more"
                  >
                    See More
                    <Icon as={FiChevronDown} />
                  </Button>
                ) : null}
              </>
            )}
          </>
        )}

        <Box className="padding-bottom" height={'24px'}></Box>
      </Box>
    )
  },
)

export default SearchGridView
